import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Activity, ArrowUpRight, Server, ShoppingCart, Monitor, HardDrive, Globe } from "lucide-react";

const DAYS = 30;
const POLL_INTERVAL = 300_000; // 5 min
const CACHE_TTL = 240_000;    // 4 min cache
const MINUTES_PER_DAY = 1440;
const UPTIME_API = "/uptimerobot/getMonitors/";

const MAIN_KEY = "m802879904-e0d32ee453ee8d9a4fa9d16d";
const SERVICES = [
  { name: "Game Panel", apiKey: "m802879822-bb8d2181bc3b1e5d28b3466e", icon: Server },
  { name: "Tienda", apiKey: "m802879872-9cce113c18d13f099fbd10e8", icon: ShoppingCart },
  { name: "Panel VPS", apiKey: "m802879874-b435197210ecea9543f701cf", icon: Monitor },
  { name: "Dedicados", apiKey: "m802879875-af4aa38fbcd5e1d57031197a", icon: HardDrive },
  { name: "cPanel", apiKey: "m802879883-a8014d794365109fa954dd0a", icon: Globe },
];

const MONTH_NAMES = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];

function buildDailyRanges() {
  const ranges = [];
  const dates = [];
  const now = new Date();
  for (let i = DAYS - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    d.setHours(0, 0, 0, 0);
    const start = Math.floor(d.getTime() / 1000);
    const end = start + 86400 - 1;
    ranges.push(`${start}_${end}`);
    dates.push(d);
  }
  return { rangeStr: ranges.join("-"), dates };
}

function formatDate(d) {
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`;
}

// ---------- Cache helpers ----------
function cacheGet(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return null;
    return data;
  } catch { return null; }
}
function cacheSet(key, data) {
  try { localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data })); } catch {}
}

// ---------- Single API call (with cache) ----------
async function apiCall(apiKey, extraParams = {}) {
  const cacheKey = `osx_uptime_${apiKey}`;
  const cached = cacheGet(cacheKey);
  if (cached) return cached;

  const params = new URLSearchParams();
  params.append("api_key", apiKey);
  params.append("format", "json");
  Object.entries(extraParams).forEach(([k, v]) => params.append(k, String(v)));

  const res = await fetch(UPTIME_API, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params.toString(),
  });
  if (!res.ok) throw new Error(`API ${res.status}`);
  const data = await res.json();
  if (data.stat !== "ok") throw new Error("API stat not ok");
  cacheSet(cacheKey, data);
  return data;
}

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

const MiniMonitor = ({ service, data }) => {
  const Icon = service.icon;
  const loading = !data;
  const isUp = data?.status === 2;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="p-4 rounded-xl flex items-center gap-3"
      style={{
        background: "var(--osx-surface)",
        border: "1px solid var(--osx-border-strong)",
      }}
    >
      <div
        className="shrink-0 h-9 w-9 rounded-lg flex items-center justify-center"
        style={{
          background: loading ? "color-mix(in oklab, #f59e0b 12%, transparent)" : isUp ? "color-mix(in oklab, #10b981 12%, transparent)" : "color-mix(in oklab, #ef4444 12%, transparent)",
          border: `1px solid ${loading ? "rgba(245,158,11,0.2)" : isUp ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)"}`,
        }}
      >
        <Icon size={16} style={{ color: loading ? "#f59e0b" : isUp ? "#10b981" : "#ef4444" }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" style={{ color: "var(--osx-text)" }}>{service.name}</div>
        <div className="text-[10px] font-mono" style={{ color: "var(--osx-text-muted)" }}>
          {loading ? "Verificando..." : data ? `${data.uptime30}% · ${data.latency !== null ? `${data.latency}ms` : "—"}` : "Sin datos"}
        </div>
      </div>
      <span className="relative inline-flex h-2.5 w-2.5 shrink-0">
        <span
          className="absolute inset-0 rounded-full animate-ping"
          style={{ background: loading ? "#f59e0b" : isUp ? "#10b981" : "#ef4444", opacity: 0.6 }}
        />
        <span
          className="relative inline-flex h-2.5 w-2.5 rounded-full"
          style={{ background: loading ? "#f59e0b" : isUp ? "#10b981" : "#ef4444" }}
        />
      </span>
    </motion.div>
  );
};

export const UptimeStatus = () => {
  const [loading, setLoading] = useState(true);
  const [monitorStatus, setMonitorStatus] = useState(null);
  const [uptimeRatios, setUptimeRatios] = useState({ d1: "—", d7: "—", d30: "—", d90: "—" });
  const [avgLatency, setAvgLatency] = useState(null);
  const [days, setDays] = useState([]);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [serviceData, setServiceData] = useState({});

  const loadAll = useCallback(async () => {
    const { rangeStr, dates } = buildDailyRanges();

    // 1) Main monitor
    try {
      const data = await apiCall(MAIN_KEY, {
        custom_uptime_ranges: rangeStr,
        custom_uptime_ratios: "1-7-30-90",
        response_times: 1,
        response_times_limit: 1,
      });
      if (data.monitors?.length) {
        const mon = data.monitors[0];
        setMonitorStatus(mon.status);
        if (mon.custom_uptime_ratio) {
          const [d1, d7, d30, d90] = mon.custom_uptime_ratio.split("-");
          setUptimeRatios({ d1, d7, d30, d90 });
        }
        if (mon.response_times?.length) setAvgLatency(mon.response_times[0].value);
        if (mon.custom_uptime_ranges) {
          const percents = mon.custom_uptime_ranges.split("-").map(Number);
          setDays(dates.map((date, idx) => {
            const uptime = percents[idx] ?? 100;
            return { date, uptime, downtimeMin: Math.round((100 - uptime) / 100 * MINUTES_PER_DAY) };
          }));
        }
        setLoading(false);
      }
    } catch { setLoading(false); }

    // 2) Service monitors — sequential with 2s delay between each
    const newServiceData = {};
    for (const svc of SERVICES) {
      try {
        await delay(2000);
        const data = await apiCall(svc.apiKey, {
          custom_uptime_ratios: "30",
          response_times: 1,
          response_times_limit: 1,
        });
        if (data.monitors?.length) {
          const mon = data.monitors[0];
          newServiceData[svc.apiKey] = {
            status: mon.status,
            uptime30: mon.custom_uptime_ratio || "—",
            latency: mon.response_times?.[0]?.value ?? null,
          };
        }
      } catch {}
      setServiceData((prev) => ({ ...prev, ...newServiceData }));
    }
  }, []);

  useEffect(() => {
    loadAll();
    const id = setInterval(loadAll, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [loadAll]);

  const isUp = monitorStatus === 2;
  const isChecking = loading;

  return (
    <section className="relative py-20 sm:py-24" data-testid="uptime-status">
      <div className="osx-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="relative p-6 sm:p-10 rounded-3xl overflow-hidden"
          style={{
            background: "var(--osx-surface)",
            border: "1px solid var(--osx-border-strong)",
            boxShadow: "var(--osx-shadow-2)",
          }}
        >
          <div className="osx-orbs" aria-hidden>
            <div className="osx-orb osx-orb--1" style={{ opacity: 0.2 }} />
          </div>

          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ background: "var(--osx-surface-2)", border: "1px solid var(--osx-border-strong)", color: "var(--osx-brand-600)" }}>
                <span className="relative inline-flex h-2 w-2">
                  <span
                    className="absolute inset-0 rounded-full animate-ping"
                    style={{ background: isChecking ? "#f59e0b" : isUp ? "#10b981" : "#ef4444" }}
                  />
                  <span
                    className="relative inline-flex h-2 w-2 rounded-full"
                    style={{ background: isChecking ? "#f59e0b" : isUp ? "#10b981" : "#ef4444" }}
                  />
                </span>
                {isChecking ? "Verificando..." : isUp ? "Todos los sistemas operativos" : "Incidencia detectada"}
              </div>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: "var(--osx-text)" }}>
                Monitor de <span className="osx-gradient-text">Estado 24/7</span>
              </h2>
              <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
                Nodos monitoreados en tiempo real para garantizar transparencia total.
                Revisá el historial de uptime, incidentes y métricas en vivo.
              </p>

              <div className="mt-6 grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--osx-text-muted)" }}>Uptime 30d</div>
                  <div className="mt-1 text-2xl font-bold font-mono" data-testid="uptime-value" style={{ color: "var(--osx-text)" }}>
                    {uptimeRatios.d30 === "—" ? "—" : `${uptimeRatios.d30}%`}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--osx-text-muted)" }}>Latencia</div>
                  <div className="mt-1 text-2xl font-bold font-mono" style={{ color: "var(--osx-text)" }}>
                    {avgLatency !== null ? `${avgLatency}ms` : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--osx-text-muted)" }}>Estado</div>
                  <div className="mt-1 text-2xl font-bold font-mono" style={{ color: isChecking ? "#f59e0b" : isUp ? "#10b981" : "#ef4444" }}>
                    {isChecking ? "..." : isUp ? "Online" : "Offline"}
                  </div>
                </div>
              </div>

              <a
                href="https://status.optishieldx.com/"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="status-view-button"
                className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white"
                style={{
                  background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                  boxShadow: "0 12px 28px rgba(2,132,199,0.32)",
                  transition: "transform 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <Activity size={14} />
                Ver estado de sistemas y nodos detallado
                <ArrowUpRight size={14} />
              </a>
            </div>

            <div className="lg:col-span-7">
              <div className="p-5 sm:p-6 rounded-2xl"
                style={{
                  background: "var(--osx-surface-2)",
                  border: "1px solid var(--osx-border-strong)",
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-sm font-semibold" style={{ color: "var(--osx-text)" }}>
                      Nodo Principal
                    </div>
                    <div className="text-xs" style={{ color: "var(--osx-text-muted)" }}>
                      Monitoreo global · checks cada 5 min
                    </div>
                  </div>
                  <span className="text-xs font-mono px-2 py-1 rounded-md"
                    style={{
                      background: isChecking
                        ? "color-mix(in oklab, #f59e0b 15%, transparent)"
                        : isUp
                          ? "color-mix(in oklab, #10b981 15%, transparent)"
                          : "color-mix(in oklab, #ef4444 15%, transparent)",
                      color: isChecking ? "#d97706" : isUp ? "#059669" : "#dc2626",
                      border: `1px solid ${isChecking ? "rgba(245,158,11,0.28)" : isUp ? "rgba(16,185,129,0.28)" : "rgba(239,68,68,0.28)"}`,
                    }}>
                    {isChecking ? "Checking..." : isUp ? "Operational" : "Down"}
                  </span>
                </div>

                {/* Barras diarias */}
                <div className="flex items-end gap-[3px] h-14">
                  {(days.length ? days : Array(DAYS).fill(null)).map((day, i) => {
                    const isEmpty = !day;
                    const uptime = day ? day.uptime : 100;
                    const isFullUp = uptime >= 99.99;
                    const isMajorDown = uptime < 85;
                    const isPartialDown = !isFullUp && !isMajorDown;
                    const barHeight = isEmpty ? 20 : Math.max(40, uptime);

                    let bg = "linear-gradient(180deg, var(--osx-brand-400), var(--osx-brand-600))";
                    if (isEmpty) bg = "var(--osx-border)";
                    else if (isMajorDown) bg = "linear-gradient(180deg, #ef4444, #dc2626)";
                    else if (isPartialDown) bg = "linear-gradient(180deg, #fbbf24, #f59e0b)";

                    return (
                      <div
                        key={`bar-${i}`}
                        className="relative flex-1 flex items-end"
                        style={{ height: "100%" }}
                        onMouseEnter={() => setHoveredBar(i)}
                        onMouseLeave={() => setHoveredBar(null)}
                      >
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: `${barHeight}%` }}
                          transition={{ duration: 0.4 }}
                          className="w-full rounded-sm"
                          style={{
                            background: bg,
                            opacity: isEmpty ? 0.3 : isPartialDown ? 0.95 : isMajorDown ? 0.9 : 0.85,
                          }}
                        />
                        {/* Tooltip */}
                        {hoveredBar === i && day && (
                          <div
                            className="absolute bottom-full left-1/2 mb-2 px-2.5 py-1.5 rounded-lg text-[10px] font-mono whitespace-nowrap z-50 pointer-events-none"
                            style={{
                              transform: "translateX(-50%)",
                              background: "var(--osx-surface)",
                              border: "1px solid var(--osx-border-strong)",
                              boxShadow: "var(--osx-shadow-2)",
                              color: "var(--osx-text)",
                            }}
                          >
                            <div className="font-semibold">{formatDate(day.date)}</div>
                            {day.downtimeMin > 0 ? (
                              <div style={{ color: "#f59e0b" }}>Caída: {day.downtimeMin} min</div>
                            ) : (
                              <div style={{ color: "#10b981" }}>100% operativo</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Etiquetas de fecha */}
                <div className="mt-2 flex justify-between text-[10px] font-mono" style={{ color: "var(--osx-text-muted)" }}>
                  <span>{days.length ? formatDate(days[0].date) : "30 días"}</span>
                  <span>{days.length ? formatDate(days[days.length - 1].date) : "Hoy"}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Mini monitores de servicios */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {SERVICES.map((svc) => (
            <MiniMonitor key={svc.name} service={svc} data={serviceData[svc.apiKey] || null} />
          ))}
        </div>
      </div>
    </section>
  );
};
